<template>
  <loading-wrapper :is-loading="!supplierData">
    <div id="container">
      <div id="title-container">
        <h2>{{ $t('generic.supplier', 1) }}</h2>
        <h1>{{ supplierData?.name }}</h1>
      </div>

      <PVTabs v-if="supplierData" v-model:value="activeTab" lazy>
        <PVTabList>
          <router-link :to="getTabLink(SupplierDetailsTab.INFORMATION)">
            <PVTab :value="SupplierDetailsTab.INFORMATION">
              {{ $t('supplierDetails.tabs.informations') }}
            </PVTab>
          </router-link>
          <router-link :to="getTabLink(SupplierDetailsTab.COWORKERS)">
            <PVTab :value="SupplierDetailsTab.COWORKERS">
              {{ $t('supplierDetails.tabs.coworkers') }}
            </PVTab>
          </router-link>
          <router-link :to="getTabLink(SupplierDetailsTab.AUTH_TOKENS)">
            <PVTab :value="SupplierDetailsTab.AUTH_TOKENS">
              {{ $t('supplierDetails.tabs.authTokens') }}
            </PVTab>
          </router-link>
        </PVTabList>
        <PVTabPanels>
          <PVTabPanel :value="SupplierDetailsTab.INFORMATION">
            <supplier-information :supplier-data="supplierData" />
          </PVTabPanel>
          <PVTabPanel :value="SupplierDetailsTab.COWORKERS">
            <suppliers-coworkers :supplier-data="supplierData" />
          </PVTabPanel>
          <PVTabPanel :value="SupplierDetailsTab.AUTH_TOKENS">
            <supplier-tokens-management :supplier-id="props.id" />
          </PVTabPanel>
        </PVTabPanels>
      </PVTabs>
    </div>
  </loading-wrapper>
</template>

<script setup lang="ts">
  import { ref, watch, onMounted } from 'vue';
  import axios from 'axios';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { ISupplier } from 'types';
  import SupplierInformation from '../../components/SupplierInformation/SupplierInformation.vue';
  import SuppliersCoworkers from '../../components/SuppliersCoworkers/SuppliersCoworkers.vue';
  import endpointsList from '../../helpers/endpointsList';
  import { useTabs } from '@/composables/useTabs';
  import { Route, routeParams } from '@/routes';
  import { SupplierDetailsTab } from './SupplierDetails.types';
  import { RouteLocationRaw } from 'vue-router';

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const props = defineProps<{ id: string }>();

  const rows = ref<number>(0);
  const supplierData = ref<ISupplier | null>(null);

  const { activeTab } = useTabs(
    Route.SUPPLIER_DETAILS,
    SupplierDetailsTab,
    SupplierDetailsTab.INFORMATION,
  );
  function getTabLink(tab: SupplierDetailsTab): RouteLocationRaw {
    return {
      name: Route.SUPPLIER_DETAILS,
      params: {
        [routeParams[Route.SUPPLIER_DETAILS].supplierId]: props.id,
        [routeParams[Route.SUPPLIER_DETAILS].tab]: tab,
      },
    };
  }

  watch(supplierData, (newValue) => {
    if (newValue) {
      rows.value = newValue.coworkers.length;
    }
  });

  async function getSupplierData() {
    try {
      const response = await axios.get(
        endpointsList.suppliers.getSupplierById(props.id),
      );
      supplierData.value = response.data;
    } catch {
      createErrorToast(
        t('supplierdetails.informations.couldNotGetSupplierDetails.error'),
      );
    }
  }

  onMounted(() => {
    getSupplierData();
  });
</script>

<style scoped lang="scss">
  #container {
    #title-container {
      margin-bottom: 50px;

      h2 {
        font-weight: 500;
        font-size: 36px;
        margin-bottom: 10px;
        line-height: 36px;
      }

      h1 {
        font-weight: 700;
        font-size: 50px;
        line-height: 36px;
      }
    }
  }
</style>
